import { LogOut } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { confirmMfaSetup, startMfaSetup } from '@/api/auth/auth.api'
import { useLogout } from '@/api/auth/mutations/useLogout'
import { LanguageSelect } from '@/components/LanguageSelect'
import { EMPTY_MFA_DIGITS, focusMfaCodeInput, MfaCodeInput } from '@/components/mfa/MfaCodeInput'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'

type MfaSetupPanelProps = {
  onComplete: () => void
  onCancel?: () => void
  /** When true, user cannot dismiss without completing setup */
  required?: boolean
}

export function MfaSetupPanel({ onComplete, onCancel, required = false }: MfaSetupPanelProps) {
  const { t } = useTranslation()
  const [secretCode, setSecretCode] = useState('')
  const [otpauthUrl, setOtpauthUrl] = useState('')
  const [codeDigits, setCodeDigits] = useState<string[]>([...EMPTY_MFA_DIGITS])
  const [isLoading, setIsLoading] = useState(true)
  const [isConfirming, setIsConfirming] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const loadGeneration = useRef(0)
  const mfaInputIdPrefix = 'mfa-setup-digit'

  const logout = useLogout()

  const loadSetup = async () => {
    const generation = ++loadGeneration.current
    try {
      setIsLoading(true)
      setErrorMessage('')
      setCodeDigits([...EMPTY_MFA_DIGITS])
      const data = await startMfaSetup()
      if (generation !== loadGeneration.current) return
      setSecretCode(data.secretCode)
      setOtpauthUrl(data.otpauthUrl)
    } catch (error) {
      if (generation !== loadGeneration.current) return
      const message = (error as { msg?: string }).msg
      setErrorMessage(message || t('mfa.setupLoadError'))
    } finally {
      if (generation === loadGeneration.current) setIsLoading(false)
    }
  }

  useEffect(() => {
    void loadSetup()
    return () => {
      loadGeneration.current += 1
    }
    // Load once on mount; regenerate via explicit button / failed confirm.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onDigitsChange = (digits: string[]) => {
    setCodeDigits(digits)
    setErrorMessage('')
  }

  const onConfirm = async (e: React.FormEvent) => {
    e.preventDefault()
    const code = codeDigits.join('')
    if (code.length !== 6) {
      setErrorMessage(t('mfa.codeRequired'))
      return
    }
    try {
      setIsConfirming(true)
      setErrorMessage('')
      await confirmMfaSetup(code)
      onComplete()
    } catch (error) {
      const message = (error as { msg?: string }).msg
      setErrorMessage(message || t('mfa.invalidCode'))
      setIsConfirming(false)
      setCodeDigits([...EMPTY_MFA_DIGITS])
      focusMfaCodeInput(mfaInputIdPrefix)
    }
  }

  const topBar = (
    <div className='flex w-full items-center justify-between'>
      <Button type='button' variant='ghost' size='sm' onClick={logout} disabled={isConfirming}>
        <LogOut className='size-4' />
        {t('mfa.logout')}
      </Button>
      <LanguageSelect />
    </div>
  )

  if (isLoading) {
    return (
      <div className='flex w-full flex-col items-center gap-4 py-8'>
        {topBar}
        <Spinner />
        <p className='text-sm text-gray-600'>{t('mfa.loadingSetup')}</p>
      </div>
    )
  }

  return (
    <div className='flex w-full flex-col items-center gap-4'>
      {topBar}
      <h2 className='text-lg font-medium text-gray-900'>{t('mfa.setupTitle')}</h2>
      <p className='text-center text-sm text-gray-600'>{t('mfa.setupDescription')}</p>

      {errorMessage && (
        <div className='w-full rounded-lg border border-red-200 bg-red-50 p-3'>
          <p className='text-center text-sm text-red-600'>{errorMessage}</p>
        </div>
      )}

      {otpauthUrl && (
        <div className='rounded-lg border border-gray-200 bg-white p-4'>
          <QRCodeSVG value={otpauthUrl} size={180} level='M' />
        </div>
      )}

      {secretCode && (
        <div className='w-full text-center'>
          <p className='mb-1 text-xs text-gray-500'>{t('mfa.manualSecret')}</p>
          <code className='rounded bg-gray-100 px-2 py-1 text-sm tracking-wider break-all'>{secretCode}</code>
        </div>
      )}

      <form onSubmit={onConfirm} className='flex w-full flex-col items-center gap-4'>
        <p className='text-sm text-gray-600'>{t('mfa.enterSetupCode')}</p>
        <MfaCodeInput idPrefix={mfaInputIdPrefix} value={codeDigits} onChange={onDigitsChange} disabled={isConfirming} />

        <Button type='submit' loading={isConfirming} disabled={isConfirming} size='lg' className='w-full'>
          {t('mfa.confirmSetup')}
        </Button>

        <Button type='button' variant='link' onClick={() => void loadSetup()} disabled={isConfirming}>
          {t('mfa.refreshQr')}
        </Button>

        {!required && onCancel && (
          <Button type='button' variant='link' onClick={onCancel}>
            {t('mfa.cancel')}
          </Button>
        )}
      </form>
    </div>
  )
}
